import * as _ from 'lodash-es';
import Elmahio from 'elmah.io.javascript';
import Swal from 'sweetalert2';
import { useScriptTag } from '@vueuse/core';
import { closeModal } from 'jenesius-vue-modal';
import { App } from 'vue';
import { User } from 'oidc-client-ts';
import { ResourceConfig } from '@repo/core/resources';
import { AppConstants } from '@repo/core/constants';
import { useDxFireSheet } from '@/utils';
import { useAppStore, useAuthStore } from '@repo/vue/stores';
import { useEventStore, useRootStore, useTabStore } from '@/stores';

export class SetupPlugin {
  install(app: App) {
    const router = app.config.globalProperties.$router;
    const pinia = app.config.globalProperties.$pinia;
    const primevue = app.config.globalProperties.$primevue;

    // stores
    const authStore = useAuthStore(pinia);
    const appStore = useAppStore(pinia);
    const eventStore = useEventStore(pinia);
    const rootStore = useRootStore(pinia);
    const tabStore = useTabStore(pinia);

    // logging
    const elmahLoggingEnabled = _.isEqual(ResourceConfig.enableElmahLogging, 'true');
    let logger = {} as Elmahio;

    if (elmahLoggingEnabled) {
      logger = new Elmahio({ apiKey: ResourceConfig.elmahApiKey, logId: ResourceConfig.elmahLogId });

      logger.on('message', (msg) => {
        try {
          msg.application = 'dxcloud.vue.web';
          if (!msg.data) msg.data = [];
          msg.url = location.href;
          if (rootStore.userProfileId) {
            msg.data.push({ key: 'UserProfileId', value: rootStore.userProfileId });
          }
          if (rootStore.practiceId) {
            msg.data.push({ key: 'PracticeId', value: rootStore.practiceId });
          }
          if (rootStore.clientId) {
            msg.data.push({ key: 'ClientId', value: rootStore.clientId });
          }
          if (rootStore.financialYearId) {
            msg.data.push({ key: 'FinancialYearId', value: rootStore.financialYearId });
          }
          if (rootStore.supportAccessRequestId) {
            msg.data.push({ key: 'SupportAccessRequestId', value: rootStore.supportAccessRequestId });
          }
          if (tabStore.activeTab?.id) {
            msg.data.push({ key: 'ActiveTab', value: JSON.stringify(tabStore.activeTab) });
          }

          msg.category = 'Vue';
          msg.url = location.href;
          msg.user = authStore.user?.profile?.email ?? '';
        } catch (e) {
          console.error('Elmah: ', e);
        }
      });

      // set elmah variable
      window.Elmahio = Elmahio;

      // log startup info
      // logger.information('DxCloud Web project started!');
    }

    app.config.throwUnhandledErrorInProduction = true;

    app.config.errorHandler = async (err, instance, info) => {
      if (elmahLoggingEnabled && _.isError(err)) {
        logger.error(err.message, err);
      } else {
        console.error(err);
      }

      if (import.meta.env.MODE !== 'production' && import.meta.env.MODE !== 'ci') {
        await closeModal();

        Swal.fire({
          title: 'Error!',
          text: err as string,
          icon: 'error'
        }).then((r) => r);
      }
    };

    // methods
    const clearRootStoreData = () => {
      const search = window.location.search;
      if ((_.includes(search, 'code=') || _.includes(search, 'error=')) && _.includes(search, 'state=')) {
        rootStore.clearCurrentUser();
        rootStore.clearCurrentClient();
        rootStore.clearCurrentEntity();
        rootStore.clearCurrentPractice();
        rootStore.clearCurrentTicket();
        rootStore.clearCurrentYear();
        rootStore.clearCurrentTabs();
      }
    };

    const setEventStoreData = (user: User) => {
      const eventData = eventStore.eventData;
      eventData.userProfileId = user.profile?.sub;
      eventData.accessToken = user.access_token;
      eventData.expires = user.expires_at;

      eventStore.$patch({
        eventData
      });
    };

    // hooks
    router.isReady().then(async () => {
      authStore.$subscribe(
        (mutation, state) => {
          const user = state.user;
          if (user?.access_token) {
            if (user && user.access_token) {
              clearRootStoreData();
              setEventStoreData(user);
            }
          }
        },
        { immediate: true }
      );

      appStore.$subscribe(
        (mutation, state) => {
          console.log('scale', state.scale);
          console.log('isDark', state.isDark);
          console.log('locale', state.locale);
          console.log('inputStyle', state.inputStyle);

          if (state.inputStyle) {
            primevue.config.inputStyle = state.inputStyle as 'outlined' | 'filled' | undefined;
            document.body.className = 'p-input-' + state.inputStyle;
          }

          if (appStore.isIFrame) {
            document.documentElement.className = 'app-light';
            document.documentElement.style.fontSize = AppConstants.defaultScale + 'px';
          } else {
            document.documentElement.className = state.isDark ? 'app-dark' : 'app-light';
            document.documentElement.style.fontSize = state.scale + 'px';
          }

          const element = document.getElementById('theme-link') as HTMLLinkElement;

          if (element) {
            if (state.isDark && !appStore.isIFrame) {
              const color = ResourceConfig.darkThemeColor;
              element.href = `/themes/theme-dark/${color}/theme.css`;
            } else {
              const color = ResourceConfig.lightThemeColor;
              element.href = `/themes/theme-light/${color}/theme.css`;
            }
          }
        },
        { immediate: true }
      );

      if (!appStore.isIFrame) {
        router.beforeResolve((to: any, from: any, next: any) => {
          const isClientProcess = _.some(to.matched, (result) => result.name === 'process');
          if (isClientProcess && (!rootStore.practiceId || !rootStore.clientId)) {
            console.warn('You no longer have a valid client file selected. Try opening client file again.');
            next({ name: 'clients', replace: true });
          } else {
            next();
          }
        });
        // TODO: implement Google Tag Manager
        // router.afterEach((to, from, failure) => {
        //   if (!failure) sendToAnalytics(to.fullPath);
        // });
      }

      // load firesheet
      const fs = useDxFireSheet();
      fs.loadFiresheetJs();

      // script tags
      const { load: loadDraftworxEnums } = useScriptTag(
        `${ResourceConfig.serverBase}enums`,
        () => {
          console.log('Draftworx enums loaded');
        },
        { manual: true }
      );
      const { load: loadClientPortalEnums } = useScriptTag(
        `${ResourceConfig.clientPortalServerBase}enums`,
        () => {
          console.log('ClientPortal enums loaded');
        },
        { manual: true }
      );

      // load enums
      try {
        await loadDraftworxEnums();
        await loadClientPortalEnums();
      } catch (e: any) {
        console.error('failed to load enums');
      } finally {
        app.mount('#app');
      }
    });
  }
}
